import { API_ROUTES } from "@/constants/api";
import { QUERY_KEYS } from "@/constants/config";
import { apiGet, apiPost } from "@/lib/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  OrderDetail,
  OrderRow,
  Paginated,
  PaymentMethod,
  SettlementType,
} from "@bizy/core";

export interface OrderListParams {
  page?: number;
  pageSize?: number;
  status?: string;
  q?: string;
}

const toQuery = (params: OrderListParams): string => {
  const search = new URLSearchParams();
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("pageSize", String(params.pageSize));
  if (params.status) search.set("status", params.status);
  if (params.q) search.set("q", params.q);
  const query = search.toString();
  return query.length > 0 ? `?${query}` : "";
};

export interface CreateOrderPayload {
  customer_name: string;
  customer_phone?: string | null;
  customer_email?: string | null;
  settlement_type: SettlementType;
  paid_amount: number;
  notes?: string | null;
  due_at?: string | null;
  items: { product_id: string; variant_id?: string | null; quantity: number }[];
}

export interface SettlePaymentPayload {
  amount: number;
  method: PaymentMethod;
  reference?: string | null;
  notes?: string | null;
}

export interface ReturnOrderPayload {
  note?: string | null;
  lines: { order_item_id: string; quantity: number; damaged_quantity: number }[];
}

export const useOrders = (workspaceId: string | undefined, params: OrderListParams = {}) =>
  useQuery({
    queryKey: [QUERY_KEYS.orders, workspaceId ?? "none", params],
    enabled: Boolean(workspaceId),
    queryFn: () =>
      apiGet<Paginated<OrderRow>>(`${API_ROUTES.orders(workspaceId as string)}${toQuery(params)}`),
  });

export const useOrder = (workspaceId: string | undefined, orderId: string | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.order, workspaceId ?? "none", orderId ?? "none"],
    enabled: Boolean(workspaceId && orderId),
    queryFn: () =>
      apiGet<OrderDetail>(API_ROUTES.order(workspaceId as string, orderId as string)),
  });

export const useCreateOrder = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOrderPayload) =>
      apiPost<OrderDetail>(API_ROUTES.orders(workspaceId), payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.orders, workspaceId] }),
  });
};

export const useSettlePayment = (workspaceId: string, orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SettlePaymentPayload) =>
      apiPost<OrderDetail>(API_ROUTES.orderPayments(workspaceId, orderId), payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.order, workspaceId, orderId] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.orders, workspaceId] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.invoices, workspaceId] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.analytics, workspaceId] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.logs, workspaceId] });
    },
  });
};

export const useCancelOrder = (workspaceId: string, orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) =>
      apiPost<OrderDetail>(API_ROUTES.orderCancel(workspaceId, orderId), { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.order, workspaceId, orderId] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.orders, workspaceId] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.products, workspaceId] });
    },
  });
};

export const useReturnOrder = (workspaceId: string, orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReturnOrderPayload) =>
      apiPost<OrderDetail>(API_ROUTES.orderReturns(workspaceId, orderId), payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.order, workspaceId, orderId] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.orders, workspaceId] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.products, workspaceId] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.logs, workspaceId] });
    },
  });
};
